<template>
  <div class="reviews-container" :class="{ site: $config.project === 'site', 'fa': $config.platform === 'fa', shortened }">
    <div v-if="!shortened" class="rating">
      <div class="total-rating">
        <p class="h2">
          {{ rating }}<span>/5</span>
        </p>
        <RatingStars :rating="reviews.rating" />
        <div class="ratings">
          ({{ reviews.ratings }} rating{{ reviews.ratings > 1 ? 's' : '' }})
        </div>
      </div>
      <div class="rating-by-stars">
        <div v-for="i in [5, 4, 3, 2, 1]" :key="i">
          <span class="stars">{{ i }} star{{ i > 1 ? 's' : '' }}</span>
          <div class="progress-wrapper">
            <div class="progress" :style="{ width: `${reviews[`rating${i}Percent`]}%` }" />
          </div>
          <span class="rating-reviews">{{ reviews[`rating${i}`] }} ({{ reviews[`rating${i}Percent`] }}%)</span>
        </div>
      </div>
    </div>
    <div class="reviews">
      <Search v-if="!shortened" placeholder="Search reviews" @search="v => search = v">
        <Filters
          slot="filters"
          type="filter-icon"
          :filters="filters"
          @change="filtrate"
        />
        <Filters
          v-if="$store.state.layout === 'desktop'"
          slot="filters"
          type="sort-icon"
          :filters="sort"
          @change="doSort"
        />
      </Search>

      <div
        v-for="(review, i) in reviewsList"
        :id="`review_${i}`"
        :key="i"
        class="review"
      >
        <div class="user">
          <nuxt-link v-if="$config.project === 'lms'" :to="`/profile/${review.userSlug}/`">
            <img :src="review.userImage" :alt="review.userName">
          </nuxt-link>
          <a v-else :href="`${$config.lmsUrl}/profile/${review.userSlug}/`">
            <img :src="review.userImage" :alt="review.userName">
          </a>
          <div class="name">
            <nuxt-link v-if="$config.project === 'lms'" :to="`/profile/${review.userSlug}/`">
              {{ review.userName }}
            </nuxt-link>
            <a v-else :href="`${$config.lmsUrl}/profile/${review.userSlug}/`">
              {{ review.userName }}
            </a>
          </div>
        </div>
        <div class="content">
          <div class="rating">
            <RatingStars :rating="review.rating" />
            <small class="date">{{ review.reviewCreated }}</small>
          </div>
          <div
            v-if="review.review"
            ref="ratingContent"
            class="rating-content"
            :class="{ expanded: review.seeMore, 'see-more': review.seeMoreBtn && !review.seeMore }"
            :inner-html.prop="review.review | highlightKeyword(search)"
          />
          <span v-if="review.seeMore || review.seeMoreBtn" @click="toggleSeeMore(i)">
            {{ !review.seeMore ? 'See more' : 'See less' }}
          </span>
        </div>
      </div>

      <div v-if="!reviewsList.length" class="no-reviews">
        No reviews
      </div>

      <Pagination
        v-if="reviewsCount > 3 && !shortened"
        :page="page"
        :total="reviewsCount"
        :per-page="3"
        align="center"
        :links="false"
        @pagechanged="changePage"
      />
    </div>
  </div>
</template>

<script>
import RatingStars from '../RatingStars';
import Search from '../../components/Search';
import Filters from '../../components/Filters';
import Pagination from '../../components/Pagination';

export default {
  components: {
    RatingStars,
    Search,
    Filters,
    Pagination,
  },
  filters: {
    highlightKeyword(v, keyword) {
      const regex = new RegExp(keyword, 'i');

      const [m = ''] = v.match(regex) || [];

      if (m !== '') {
        return v.replace(m, `<strong>${m}</strong>`);
      }
      return v;
    },
  },
  props: {
    shortened: { type: Boolean },
    reviews: { type: Object, required: true },
  },
  data() {
    return {
      page: 1,
      search: '',
      filter: '',
      order: '',
      reviewsList: this.reviews.list,
      reviewsCount: this.reviews.count,
    };
  },
  computed: {
    filters() {
      const rating = {
        name: 'Filter by rating',
        type: 'multiple',
        values: {
          rating_5: { text: '5 stars' },
          rating_4: { text: '4 stars' },
          rating_3: { text: '3 stars' },
          rating_2: { text: '2 stars' },
          rating_1: { text: '1 star' },
        },
      };

      if (this.$store.state.layout === 'mobile') {
        return { rating, order: this.sort.order };
      }

      return { rating };
    },
    sort() {
      return {
        order: {
          name: 'Sort',
          values: {
            oldest: { text: 'Date posted', selected: true },
            rating: { text: 'Rating' },
          },
        },
      };
    },
    rating() {
      if (this.reviews.rating.toFixed(1) % 1 === 0) {
        return this.reviews.rating.toFixed(0);
      }

      return this.reviews.rating.toFixed(1);
    },
  },
  watch: {
    review: {
      handler() {
        this.$nextTick(() => {
          this.setSeeMoreButtons();
        });
      },
      deep: true,
    },
  },
  created() {
    if (process.client) {
      this.$watch(({ search }) => search, () => {
        this.page = 1;
        this.fetchReviews();
      }, { immediate: false });
    }
  },
  mounted() {
    setTimeout(() => {
      this.setSeeMoreButtons();
    }, 500);

    this.$nextTick(() => {
      if (!this.shortened) {
        this.fetchReviews();
      }
    });
  },
  methods: {
    toggleSeeMore(i) {
      this.$set(this.reviewsList[i], 'seeMore', !this.reviewsList[i].seeMore);
    },
    async fetchReviews() {
      const filter = this.filter ? `&${this.filter}` : '';
      const order = this.order ? `${this.order}` : 'filter[order]=oldest';

      const reviews = await this.$axios.$get(`/courses/${this.reviews.courseId}/reviews?page=${this.page}&${order}&q=${this.search || ''}${filter}`);

      this.reviewsList = reviews.list;
      this.reviewsCount = reviews.count;

      this.$nextTick(() => {
        this.setSeeMoreButtons();
      });
    },
    setSeeMoreButtons() {
      const reviews = this.$el.querySelectorAll('.review .rating-content');
      reviews.forEach((r) => {
        if (r && r.closest('.review')) {
          const id = r.closest('.review').id.replace('review_', '');
          this.$set(this.reviewsList[id], 'seeMoreBtn', r.scrollHeight > 88);
        }
      });
    },
    changePage(page) {
      this.page = page;
      this.fetchReviews();
      this.$nextTick(() => {
        this.$el.querySelector('.reviews-container .rating').scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
    },
    filtrate(f) {
      this.page = 1;
      this.filter = f.params;
      this.fetchReviews();
    },
    doSort(f) {
      this.order = f.params;
      this.fetchReviews();
    },
  },
};
</script>

<style lang="scss">
.reviews-container {
  .rating {
    .total-rating {
      margin-bottom: var(--spacing-30);
      display: flex;
      align-items: baseline;
      gap: 8px;
    }

    .h2 {
      margin: 0;
      font-weight: 500;
      font-size: 50px;
      line-height: 60px;
      color: var(--main-dark-shade-0);

      span {
        color: var(--main-light-shade-4);
        font-size: 24px;
        font-weight: 600;
        line-height: 30px;
      }
    }

    .ratings {
      color: var(--main-dark-light-4);
      line-height: 150%;
    }

    .rating-by-stars {
      flex: 1;

      > div {
        display: flex;
        align-items: center;

        span.stars {
          text-align: left;
          color: var(--main-dark-light-3);
          margin-right: var(--spacing-12);
          min-width: 54px;
          flex-shrink: 1;
          font-weight: 400;
          line-height: 150%;
        }

        span.rating-reviews {
          color: var(--main-dark-light-4);
          line-height: 24px;
          margin-left: var(--spacing-12);
          max-width: 100px;
          display: inline-block;
          flex-shrink: 0;
          font-size: 14px;
          font-weight: 400;
        }

        .progress-wrapper {
          background-color: var(--main-light-shade-3);
          border-radius: 100px;
          overflow: hidden;
          height: 8px;
          position: relative;
          width: calc(100% - 118px);
          flex-shrink: 2;

          .progress {
            top: 1px;
            left: 2px;
            height: 10px;
            position: absolute;
            max-width: calc(100% - 4px);
            background-color: var(--accent);
            border-top-left-radius: 100px;
            border-bottom-left-radius: 100px;
          }
        }
      }
    }
  }

  .reviews {
    margin-top: var(--spacing-32);

    .search-wrapper {
      margin: var(--spacing-40) 0;
      position: relative;
      z-index: 2;

      .input-group {
        margin-right: 8px;

        .close-search-icon {
          margin-top: -7px;
          width: 14px;
          height: 14px;

          path[stroke] {
            stroke: var(--main-light-shade-4);
          }
        }

        input {
          height: 40px;
          border-color: var(--main-light-shade-4);
        }
      }

      .filters {
        display: flex;
        z-index: 0;

        .__filters {
          min-width: unset;

          > button {
            width: 44px;
            height: 40px;
          }
        }

        .sort-icon {
          margin-left: 8px;
        }
      }
    }

    .review {
      &:not(:last-child) {
        margin-bottom: 32px;
      }

      .user {
        display: flex;
        align-items: center;
        gap: 16px;
        margin-bottom: var(--spacing-12);

        img {
          width: 50px;
          height: 50px;
          object-fit: cover;
          border-radius: 50%;
          display: block;
        }

        .name {
          a {
            color: var(--main-dark-shade-0);
          }
        }
      }

      .content {
        width: 100%;
        text-align: left;

        .rating {
          display: flex;
          align-items: center;
          justify-content: space-between;
          margin-bottom: var(--spacing-16);

          .date {
            color: var(--main-dark-light-4);
            font-size: 14px;
            font-weight: 400;
            line-height: 130%;
          }
        }

        .rating-content {
          text-align: left;
          line-height: 150%;
          max-height: 88px;
          overflow: hidden;
          transition: max-height 0.3s ease;
          position: relative;

          strong {
            color: var(--main-light);
            background: var(--accent);
            padding: 0 var(--spacing-6) 0 2px;
            font-style: italic;
          }

          &.see-more:before {
            content: "";
            position: absolute;
            bottom: 0;
            left: 0;
            background: linear-gradient(0deg, var(--main-light) -12.31%, rgba(255, 255, 255, 0.94) 30.39%, rgba(255, 255, 255, 0) 100%);
            width: 100%;
            height: 60px;
            z-index: 1;
          }

          &.expanded {
            max-height: none;
          }
        }

        span {
          color: var(--accent);
          font-weight: 600;
          margin-top: 16px;
          display: inline-block;
          cursor: pointer;
        }
      }
    }

    .no-reviews {
      text-align: center;
      font-size: 21px;
      font-weight: 600;
      color: var(--main-light-shade-4);
    }
  }

  @include bp($bp-mobile) {
    padding: 0;
    margin-top: 80px;

    > .rating {
      flex-direction: column;
      align-items: flex-start;

      .rating-by-stars {
        margin-top: var(--spacing-30);
        width: 100%;
        max-width: 100%;

        > div {
          display: flex;
          flex-wrap: wrap;
          margin-bottom: var(--spacing-20);

          &:last-of-type {
            margin-bottom: 0;
          }

          span.stars {
            display: flex;
            order: 1;
          }

          span.rating-reviews {
            font-size: 14px;
            margin-left: 0;
            display: flex;
            order: 2;
            flex: 0 0 100%;
          }

          .progress-wrapper {
            display: flex;
            order: 3;
            flex: 0 0 100%;
          }
        }
      }
    }

    .reviews {
      .review {
        flex-direction: column;

        .user {
          width: 100%;
        }
      }
    }
  }

  &.fa {
    .rating {
      .rating-by-stars {
        .progress-wrapper {
          .progress {
            background-color: var(--accent-light-3-400);
          }
        }
      }
    }
  }
}

.fa {
  .reviews-container.site .reviews .review .content .rating-content {
    line-height: 150%;
  }
}
</style>
